/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Seconds,
  Ducks,
  PlayerLiveTimer,
  TvAsset,
  PlayerLiveTimerEvent,
  Program,
  TvAssetUtils,
  RelativeTime,
  AssetUtils,
  Asset,
  TimeUtils,
  XError,
  DrmCertificateApi,
  PlayerErrors,
  LocalStorage,
  GenericErrors,
} from '@wit/vtv-x-foundation-lib';
import useFullscreenStatus from 'hooks/useFullscreenStatus';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { useWindowBeforeunload } from 'hooks/useWindowBeforeunload';
import {
  Selectors as PlayerSelectors,
  ActionCreators as PlayerActions,
  PlaybackState,
  ReducerState as PlayerState,
} from 'store/ducks/player';
import * as ManagePin from 'store/ducks/settings/managePin';
import Constants from 'configs/Constants';
import translate from 'services/i18n/translate';
import { convertHMS } from 'services/utils/DateTimeUtils';
import NagraPlayerWrapper, { SsmSettings } from './Nagra';
import VideoUi, { EpisodeData, MediaData } from './VideoUi';
import Modal from '../Modal';
import { VisualScrubber } from '..';
import Pin from '../Modal/Pin';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { TextTrackKind, TextTrackMode } from '../../../types/libs/opySdk';
import { addPlayerErrorEvent } from '../../../store/ducks/analytics/systemEvent';
import Logger from '../../../services/debug/logger';
import { IPlayerStats } from './VideoUi/Stats';
import { useSeekingStream } from '../../../hooks/useSeekingStream';

enum ERROR_LEVEL {
  FATAL = 'fatal', // show the error to the user and dismiss the player
  ERROR = 'error', // show the error to the user and wait for player recover
  LOG = 'log', // only log the error
}

interface Props {
  setShowWrapper: (arg: boolean) => void;
  onError: () => void;
  onHide: () => void;
}

const getEpisodeData = (asset: Asset | undefined): EpisodeData => {
  const episodeData: EpisodeData = { name: asset?.title || '' };

  if (
    asset &&
    (AssetUtils.isEpisode(asset) || AssetUtils.isTvAsset(asset)) &&
    !AssetUtils.isSpecialSeasonNumber(asset.seasonNumber)
  ) {
    episodeData.seasonNumber = asset.seasonNumber;
    episodeData.episodeNumber = asset.episodeNumber;

    if (AssetUtils.isTvAsset(asset)) {
      episodeData.startDate = TimeUtils.toUnixEpoch(asset.startDate);
      episodeData.endDate = TimeUtils.toUnixEpoch(asset.endDate);
    }
  }

  return episodeData;
};

const getMediaData = (nextState: PlayerState): MediaData => {
  return {
    controllers: {
      disable: false,
      play: nextState.trickModesEnable,
      restart: nextState.trickModesEnable,
      moveScrubber: nextState.trickModesEnable,
      subtitles: [],
      rules: {
        allowForward: nextState.trickModes?.isFFAllowed || false,
        allowRewind: nextState.trickModes?.isRWAllowed || false,
        allowPause: nextState.trickModes?.isPauseAllowed || false,
      },
    },
    episode: getEpisodeData(nextState.currentAsset),
    channel: nextState.relatedChannel
      ? {
          name: nextState.relatedChannel.title,
          logo: nextState.relatedChannel.logo?.white?.uri || '', // TODO
        }
      : undefined,
  };
};

const getLivePositionHitState = (isLiveMode: boolean, position: Seconds, liveProgress: Seconds) =>
  isLiveMode && Math.abs(liveProgress - position) <= Constants.HIT_LIVE_POSITION_THRESHOLD;

const isSameContent = (prevState: PlayerState | undefined, nextState: PlayerState) => {
  // when the state changed, checks if the playing content is still the same
  // -same live channel or other non live playing asset
  return (
    (nextState.isLivePosition &&
      prevState?.isLivePosition &&
      (prevState?.relatedChannel === nextState.relatedChannel ||
        prevState?.relatedChannel?.id === nextState.relatedChannel?.id)) ||
    (prevState?.currentAsset &&
      nextState.currentAsset &&
      prevState?.currentAsset.id === nextState.currentAsset.id &&
      prevState?.currentStream?.id === nextState?.currentStream?.id) // we need compare the streams because the same vod may have different streams
  );
};

// these vars are being cleaned when the component is destroyed
let media: MediaData = {
  controllers: {
    disable: false,
    play: true,
    restart: true,
    moveScrubber: false,
    subtitles: [],
    rules: {
      allowForward: false,
      allowRewind: false,
      allowPause: false,
    },
  },
  episode: {
    name: '',
  },
};

export default function Player({
  setShowWrapper,
  onError: onErrorCallback,
  onHide: onHideCallback,
}: Props): JSX.Element {
  const isFullScreenNative = false;
  const userPressedF11 = false;
  const fillContainer = true;
  const sliderActive = false;
  const controllersActive = true;
  const hideControllersTimeout = 5000;

  // LOCAL STATE

  const videoHtmlElementRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<NagraPlayerWrapper>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const videoDimensions = useResizeObserver(videoHtmlElementRef);
  const { isFullscreen, handleFullscreen } = useFullscreenStatus(wrapperRef);
  const [isMuted, setIsMuted] = useState(true); // by default the stream shall start as muted (tip by Nagra)
  const [overlayActive, setOverlayActive] = useState(false);
  const [subtitles, setSubtitles] = useState<TextTrack[]>([]);
  const [audio, setAudio] = useState<AudioTrack[]>([]);
  const [videoHeight, setVideoHeight] = useState('100%');
  const [scrubbingProgress, setScrubbingProgress] = useState<undefined | number>();

  const [playbackError, setPlaybackError] = useState<XError | undefined>(undefined);
  // use a ref to safe the current error to avoid player loops
  const errorRef = useRef<XError | undefined>();

  const pinSuccessCallbackRef = useRef<(() => void) | undefined>();

  // STORE STATE

  const dispatch = useDispatch();
  // DMS
  const nagraSettings = useSelector(Ducks.Dms.Selectors.getNagraSettings);
  const shakaSettings = useSelector(Ducks.Dms.Selectors.getShakaSettings);
  const isVisualScrubberOn = useSelector(Ducks.Dms.Selectors.isVisualScrubberOn);
  const isSsmEnabled = useSelector(Ducks.Dms.Selectors.isSsmEnabled);
  const fileTypesMimeTypeMap = useSelector(Ducks.Dms.Selectors.fileTypesMimeType);

  // USER
  const currentUser = useCurrentUser();

  // PLAYER
  const curPlayerState = useSelector(PlayerSelectors.data);
  const prevPlayerStateRef = useRef(curPlayerState);

  const duration = useSelector(PlayerSelectors.currentDuration);
  const progress = useSelector(PlayerSelectors.currentProgress);
  const liveProgress = useSelector(PlayerSelectors.liveProgress);
  const playbackState = useSelector(PlayerSelectors.playbackState);
  const ssmLicense = useSelector(PlayerSelectors.ssmLicense);
  const ssmError = useSelector(PlayerSelectors.ssmError);
  const watchError = useSelector(PlayerSelectors.watchError);
  const resumeError = useSelector(PlayerSelectors.resumeError);
  const isPlaybackAdult = useSelector(PlayerSelectors.isPlaybackAdult);
  const [videoStats, setVideoStats] = useState<IPlayerStats>();
  const activateStats = useSelector(PlayerSelectors.isPlayerStatsActivated);

  const [seekingStream, setSeekingStream] = useSeekingStream(progress);

  const [trickModesModal, setTrickModesModal] = useState(false);
  const trickModeDisabledNotification = useMemo(() => {
    return {
      duration: 3, // since modal notification animation is not dynamic keep the 3 seconds for now
      onClose: () => setTrickModesModal(false),
      text: translate['player.trickModeDisable'],
    };
  }, []);

  // holds the promise executor callbacks to communicate with the player license handler
  const licensePromiseRef = useRef<{
    resolve: (value: { license: string; heartbeat: number }) => void;
    reject: () => void;
  }>();

  // SSM player settings: certificate handler and license handler
  const ssmSettingsRef = useRef<SsmSettings | undefined>(
    isSsmEnabled
      ? {
          certificateHandler: DrmCertificateApi.getCertificate,
          licenseHandler: (
            drmId: string,
            challenge: string,
            isRenewal: boolean
          ): Promise<{ license: string; heartbeat: number }> => {
            dispatch(PlayerActions.licenseRequest(drmId, challenge, isRenewal));
            return new Promise((resolve, reject) => {
              licensePromiseRef.current = { resolve, reject };
            });
          },
        }
      : undefined
  );

  const liveProgressCallback = useCallback(
    (ev: PlayerLiveTimerEvent) => {
      dispatch(PlayerActions.setStreamProgress(ev.progress, ev.liveProgress));
    },
    [dispatch]
  );

  const onError = useCallback(
    (error: XError, level: ERROR_LEVEL) => {
      if (level === ERROR_LEVEL.FATAL) {
        dispatch(PlayerActions.setPlaybackState(PlaybackState.PLAYBACK_ERROR));
        setPlaybackError(error);
      } else if (level === ERROR_LEVEL.ERROR) {
        setPlaybackError(error);
      } else {
        Logger.error(error);
      }
      const { currentAsset, relatedChannel, isLivePosition } = prevPlayerStateRef.current;
      addPlayerErrorEvent(error, currentAsset, relatedChannel, isLivePosition);
    },
    [dispatch]
  );

  useEffect(() => {
    const actionError = watchError || resumeError;
    if (actionError) {
      onError(actionError, ERROR_LEVEL.FATAL);
    }
  }, [onError, watchError, resumeError]);

  useEffect(() => {
    if (isPlaybackAdult) {
      dispatch(PlayerActions.setPlaybackState(PlaybackState.STOP));
      onHideCallback();
    }
  }, [isPlaybackAdult, dispatch, onHideCallback]);

  const onPlaybackError = useCallback(
    (error?: XError) => {
      let currentError: XError;

      if (errorRef.current) {
        // check if an error was saved in the error ref and show that one
        currentError = errorRef.current;
      } else if (error) {
        // if no error saved in the error ref, fallback to the received error
        currentError = error;
      } else {
        // fallback to the default
        currentError = XError.fromCode(PlayerErrors.generic.unknownError);
      }

      // clear error ref, no longer needed
      errorRef.current = undefined;
      onError(currentError, ERROR_LEVEL.FATAL);
    },
    [onError]
  );

  const onWatchAssetChanged = useCallback(
    (prevState: PlayerState | undefined, nextState: PlayerState) => {
      if (nextState.isPlaybackAdult) {
        videoRef.current?.stop();
        PlayerLiveTimer.stop();
        return;
      }

      if (!prevState?.watch?.error && nextState.watch?.error) {
        onError(nextState.watch.error, ERROR_LEVEL.FATAL);
        return;
      }

      if (isSameContent(prevState, nextState)) {
        return; // do nothing
      }

      // stop live timer if it exists
      if (prevState?.isLiveMode && prevState?.currentAsset) {
        PlayerLiveTimer.stop();
      }

      if (nextState.currentAsset) {
        if (nextState.isLiveMode) {
          const { startDate, endDate } = nextState.currentAsset as TvAsset;
          PlayerLiveTimer.init(startDate, endDate, liveProgressCallback);
          PlayerLiveTimer.livePosition(!!nextState.isLivePosition);
        }

        media = getMediaData(nextState);
      }

      if (fileTypesMimeTypeMap && nextState.originalStream?.fileType) {
        // get the mimeType related with the target stream file type from the dms config
        const mimeType = fileTypesMimeTypeMap[nextState.originalStream.fileType];
        videoRef.current?.setPreferredMimeType(mimeType);
      }

      if (nextState.isPlaybackLocked) {
        pinSuccessCallbackRef.current = () => {
          if (nextState.currentStream) {
            videoRef.current?.setStream(nextState.currentStream.uri, nextState.currentStream.token);
          }
        };
      } else if (nextState.currentStream) {
        videoRef.current?.setStream(nextState.currentStream.uri, nextState.currentStream.token);
      }
    },
    [liveProgressCallback, onError, fileTypesMimeTypeMap]
  );

  const handleSeekSuccess = useCallback(
    (prevState: PlayerState | undefined, nextState: PlayerState, isRestart?: boolean) => {
      if (prevState?.currentStream !== nextState.currentStream && nextState.currentStream) {
        videoRef.current?.setStream(nextState.currentStream?.uri, nextState.currentStream?.token);
        PlayerLiveTimer.setPadding(nextState.paddingStart || 0, nextState.paddingEnd || 0);
        PlayerLiveTimer.livePosition(!!nextState.isLivePosition);
      } else if (nextState.targetStreamPosition !== undefined) {
        const validPosition = videoRef.current?.setPosition(nextState.targetStreamPosition, nextState.currentAsset);
        if (!validPosition) {
          onError(XError.fromCode(PlayerErrors.seek.invalidPosition), ERROR_LEVEL.LOG);
        }

        // restart non live content should play even when the state was pause
        if (isRestart && videoRef.current?.paused()) {
          videoRef.current?.play();
        }
      }
    },
    [onError]
  );

  const onRestartProcessed = useCallback(
    (prevState: PlayerState | undefined, nextState: PlayerState) => {
      if (!prevState?.restart?.error && nextState.restart?.error) {
        onError(nextState.restart.error, ERROR_LEVEL.FATAL);
        return;
      }

      handleSeekSuccess(prevState, nextState, true);
    },
    [handleSeekSuccess, onError]
  );

  const onSeekProcessed = useCallback(
    (prevState: PlayerState | undefined, nextState: PlayerState) => {
      if (!prevState?.seek?.error && nextState.seek?.error) {
        onError(nextState.seek.error, ERROR_LEVEL.FATAL);
        return;
      }

      handleSeekSuccess(prevState, nextState);
    },
    [handleSeekSuccess, onError]
  );

  const onPauseProcessed = useCallback(() => {
    videoRef.current?.pause();
  }, []);

  const onResumeProcessed = useCallback((prevState: PlayerState | undefined, nextState: PlayerState) => {
    if (prevState?.currentStream !== nextState.currentStream && nextState.currentStream) {
      videoRef.current?.setStream(nextState.currentStream?.uri, nextState.currentStream?.token);
      PlayerLiveTimer.livePosition(!!nextState.isLivePosition);
    } else {
      videoRef.current?.play();
    }
  }, []);

  // live program rollover or cont rewind rollover or cont rewind seek

  const updateLiveTimer = useCallback(
    (program: Program, isLivePosition: boolean) => {
      if (TvAssetUtils.getRelativeTime(program) !== RelativeTime.LIVE) {
        // only update the timer if it is the live program
        return;
      }

      const { startDate, endDate } = program;

      PlayerLiveTimer.init(startDate, endDate, liveProgressCallback);
      PlayerLiveTimer.livePosition(isLivePosition);

      if (prevPlayerStateRef.current.playbackState === PlaybackState.PLAYING) {
        PlayerLiveTimer.play();
      }
    },
    [liveProgressCallback]
  );

  const handleLiveProgramUpdate = useCallback(
    (program: Program | undefined, isLivePosition: boolean, isPlaybackLocked: boolean, isPlaybackHidden: boolean) => {
      if (isPlaybackHidden) {
        videoRef.current?.stop();
        PlayerLiveTimer.stop();
        return;
      }

      if (!program) {
        PlayerLiveTimer.stop();
        return;
      }

      if (isPlaybackLocked) {
        videoRef.current?.pause();

        pinSuccessCallbackRef.current = () => {
          // playback unlocked: continue playing in the current live stream
          updateLiveTimer(program, isLivePosition);
          videoRef.current?.play();
        };
      } else {
        updateLiveTimer(program, isLivePosition);
      }
    },
    [updateLiveTimer]
  );

  const onPlaybackLocked = useCallback(() => {
    videoRef.current?.pause();
    pinSuccessCallbackRef.current = () => {
      // playback unlocked: continue playing in the current stream
      videoRef.current?.play();
    };
  }, []);

  // handling license retrieving
  React.useEffect(() => {
    // if new license data is available and there is a promise waiting, then resolves it
    if (ssmLicense && licensePromiseRef.current) {
      licensePromiseRef.current.resolve(ssmLicense);
      licensePromiseRef.current = undefined;
    }
  }, [ssmLicense]);

  // handling license retrieving error
  React.useEffect(() => {
    if (ssmError) {
      // if new error is available and there is a promise waiting, then rejects it
      // because we rejects, later the player will raise playback error that will be handled as usual
      // also save the error in case of the player error is unknown
      if (licensePromiseRef.current) {
        licensePromiseRef.current.reject();
        licensePromiseRef.current = undefined;
        errorRef.current = ssmError;
      } else if (ssmError.code === GenericErrors.network.networkFailure) {
        // if it is a network error don't dismiss the player since the playback can recover
        onError(ssmError, ERROR_LEVEL.ERROR);
      } else {
        // if there is a new error but the player didn't requests a license then it is a keep session error
        // so we shall stop the playback
        onError(ssmError, ERROR_LEVEL.FATAL);
      }
    }
  }, [ssmError, onError]);

  //
  React.useEffect(() => {
    if (prevPlayerStateRef.current.watch?.processed) {
      const initialState = prevPlayerStateRef.current;

      onWatchAssetChanged(undefined, initialState);
    }
  }, [dispatch, onWatchAssetChanged]);

  // CLEAN STATE
  React.useEffect(() => {
    return () => {
      PlayerLiveTimer.stop();
      dispatch(PlayerActions.setPlaybackState(PlaybackState.STOP));
      setSeekingStream(false);
      media = {
        controllers: {
          disable: false,
          play: true,
          restart: true,
          moveScrubber: false,
          subtitles: [],
          rules: {
            allowForward: false,
            allowRewind: false,
            allowPause: false,
          },
        },
        episode: {
          name: '',
        },
      };
      dispatch(PlayerActions.clearState());
    };
  }, [dispatch, setSeekingStream]);

  useWindowBeforeunload(() => {
    PlayerLiveTimer.stop();
    dispatch(PlayerActions.setPlaybackState(PlaybackState.STOP));
  });

  React.useEffect(() => {
    const { isLiveMode } = prevPlayerStateRef.current;

    if (isLiveMode) {
      if (playbackState === PlaybackState.PLAYING) {
        PlayerLiveTimer.play();
      } else if (playbackState === PlaybackState.BUFFERING || playbackState === PlaybackState.PAUSED) {
        PlayerLiveTimer.pause();
      }
    }
  }, [playbackState]);

  React.useEffect(() => {
    // "on each component update" checks the player actions
    if (prevPlayerStateRef.current !== curPlayerState) {
      const prevState = prevPlayerStateRef.current;
      prevPlayerStateRef.current = curPlayerState;

      // rollover
      if (
        prevState.isLiveMode &&
        curPlayerState.isLiveMode && // live
        (prevState.relatedChannel === curPlayerState.relatedChannel ||
          prevState.relatedChannel?.id === curPlayerState.relatedChannel?.id) && // same channel
        !AssetUtils.equals(prevState.currentAsset, curPlayerState.currentAsset)
      ) {
        media = getMediaData(curPlayerState);
        handleLiveProgramUpdate(
          curPlayerState.currentAsset as Program | undefined,
          !!curPlayerState.isLivePosition,
          !prevState.isPlaybackLocked && curPlayerState.isPlaybackLocked, // consider only new lock state, so it will be false if already locked
          curPlayerState.isPlaybackAdult
        );
      }

      if (prevState.watch !== curPlayerState.watch && curPlayerState.watch?.processed) {
        onWatchAssetChanged(prevState, curPlayerState);
      } else if (prevState.restart !== curPlayerState.restart && curPlayerState.restart?.processed) {
        onRestartProcessed(prevState, curPlayerState);
      } else if (prevState.seek !== curPlayerState.seek && curPlayerState.seek?.processed) {
        onSeekProcessed(prevState, curPlayerState);
      } else if (prevState.pause !== curPlayerState.pause && curPlayerState.pause?.processed) {
        onPauseProcessed();
      } else if (prevState.resume !== curPlayerState.resume && curPlayerState.resume?.processed) {
        onResumeProcessed(prevState, curPlayerState);
      } else if (prevState.currentAsset !== curPlayerState.currentAsset) {
        media = getMediaData(curPlayerState);
      } else if (!prevState.isPlaybackLocked && curPlayerState.isPlaybackLocked) {
        onPlaybackLocked();
      }
    }
  }, [
    curPlayerState,
    handleLiveProgramUpdate,
    onPauseProcessed,
    onPlaybackLocked,
    onRestartProcessed,
    onResumeProcessed,
    onSeekProcessed,
    onWatchAssetChanged,
  ]);

  const restoreVolume = useCallback(async (): Promise<void> => {
    // Note: in order to be able to detect if the browser blocked the auto-play functionality,
    // only use the restore volume function before the video play method
    const localStoragePlayerVolume = await LocalStorage.getPlayerVolume();
    const localStoragePlayerIsMuted = await LocalStorage.getPlayerMuted();
    if (videoRef.current) {
      videoRef.current.setMuted(localStoragePlayerIsMuted);
      setIsMuted(localStoragePlayerIsMuted);
      dispatch(PlayerActions.setMuteState(localStoragePlayerIsMuted));
      if (localStoragePlayerVolume) {
        videoRef.current.volume(localStoragePlayerVolume);
        dispatch(PlayerActions.setVolume(localStoragePlayerVolume));
      }
    }
  }, [dispatch]);

  // NAGRA PLAYER EVENTS

  const activatePreferredSubtitle = useCallback(
    (tracks: TextTrack[]) => {
      const subtitlesEnabled = currentUser?.accessibilitySubtitles;
      const uiLocale = new Intl.Locale(translate.getLanguage());
      if (subtitlesEnabled) {
        let bestTrack = tracks.find((track) => new Intl.Locale(track.language).baseName === uiLocale.baseName);
        if (bestTrack === undefined) {
          bestTrack = tracks.find((track) => new Intl.Locale(track.language).language === uiLocale.language);
        }
        if (bestTrack) {
          bestTrack.mode = TextTrackMode.SHOWING;
        }
      }
    },
    [currentUser?.accessibilitySubtitles]
  );

  const onStreamReady = useCallback(async (): Promise<void> => {
    const { isLiveMode, targetStreamPosition, currentAsset } = prevPlayerStateRef.current;

    // setIsLoading(false);
    dispatch(PlayerActions.setPlaybackState(PlaybackState.STREAM_READY));

    if (!isLiveMode) {
      dispatch(PlayerActions.setStreamDuration(videoRef.current?.duration() || 0));
    }

    // Update Player with new Settings
    videoRef.current?.updatePlayerSettings();

    // restore the saved volume
    await restoreVolume();

    // handle here the needed seeks
    // -seek to a bookmark
    // -when there are stream switching in live mode
    if (targetStreamPosition !== undefined) {
      const validPosition = videoRef.current?.setPosition(targetStreamPosition, currentAsset);
      if (!validPosition) {
        onError(XError.fromCode(PlayerErrors.seek.invalidPosition), ERROR_LEVEL.LOG);
      }
    }

    videoRef.current?.play();

    const tempSubtitles: TextTrackList | [] = videoRef.current?.getSubtitles() || [];
    const tempAudio: AudioTrack[] = videoRef.current?.getAudio() || [];
    const finalSubtitles = [];
    const finalAudio = [];

    for (let i = 0; i < tempSubtitles.length; i += 1) {
      if (
        tempSubtitles[i].kind === TextTrackKind.SUBTITLES ||
        tempSubtitles[i].kind === TextTrackKind.SUBTITLES_HARD_OF_HEARING
      ) {
        finalSubtitles.push(tempSubtitles[i]);
      }
    }
    for (let i = 0; i < tempAudio.length; i += 1) {
      finalAudio.push(tempAudio[i]);
    }
    const sortedSubtitles = finalSubtitles.sort((a, b) => a.label.localeCompare(b.label));
    activatePreferredSubtitle(sortedSubtitles);
    setSubtitles(sortedSubtitles);
    setAudio(finalAudio.sort((a, b) => a.label.localeCompare(b.label)));
  }, [activatePreferredSubtitle, dispatch, onError, restoreVolume]);

  const onPlaybackPaused = useCallback((): void => {
    dispatch(PlayerActions.setPlaybackState(PlaybackState.PAUSED));
  }, [dispatch]);

  const onAutoPlayDenied = useCallback(
    (denied: boolean): void => {
      dispatch(PlayerActions.setAutoPlayDenied(denied));
    },
    [dispatch]
  );

  const onPlaybackPlay = useCallback((): void => {
    dispatch(PlayerActions.setPlaybackState(PlaybackState.PLAY));
  }, [dispatch]);

  const onPlaybackBuffering = useCallback((): void => {
    if (videoRef.current?.paused()) {
      // don't re-write the 'pause' playback state with the stream transition events
      // this happens when seeking while paused
      return;
    }

    dispatch(PlayerActions.setPlaybackState(PlaybackState.BUFFERING));
  }, [dispatch]);

  const onPlaybackPlaying = useCallback((): void => {
    // If there is an error and the player recovers from it, dismiss it
    setPlaybackError(undefined);

    if (videoRef.current?.paused()) {
      // don't re-write the 'pause' playback state with the stream transition events
      // this happens when seeking while paused
      return;
    }

    dispatch(PlayerActions.setPlaybackState(PlaybackState.PLAYING));

    setSeekingStream(false);
  }, [dispatch, setSeekingStream]);

  const onStreamPositionUpdated = useCallback(
    (newPosition: Seconds) => {
      const { isLiveMode } = prevPlayerStateRef.current;
      if (isLiveMode) {
        // in live forwards the progress to the live timer
        PlayerLiveTimer.setStreamProgress(newPosition);
      } else {
        dispatch(PlayerActions.setStreamProgress(newPosition));
      }

      // handlingTransitions
    },
    [dispatch]
  );

  const onBitrateChange = useCallback(
    (bitrate): void => {
      dispatch(PlayerActions.setBitrate(bitrate));
    },
    [dispatch]
  );

  // UI TRANSPORT CONTROLS EVENTS

  const handleScrubberPosition = useCallback(
    (position: Seconds | undefined): void => {
      const { trickModesEnable, isLiveMode, liveProgress: livePos } = prevPlayerStateRef.current;

      if (!trickModesEnable) {
        return;
      }

      // scrubber sends position=undefined if the trick modes are not allowed
      if (position === undefined) {
        setTrickModesModal(true);
        return;
      }

      // this.showTrickModeNotAllowedNofication();

      setSeekingStream(true);

      const hitLivePos = getLivePositionHitState(isLiveMode, position, livePos);

      if (hitLivePos) {
        // the position has hit the LIVE position
        dispatch(PlayerActions.seekRequest({ position: Constants.LIVE_POSITION_VALUE }));
      } else {
        // normal seek in a non-live stream
        dispatch(PlayerActions.seekRequest({ position }));
      }
    },
    [dispatch, setSeekingStream]
  );

  const handlePlayButton = useCallback(() => {
    const {
      playbackState: curPlaybackState,
      trickModesEnable,
      trickModes,
      isPlaybackLocked,
      isPlaybackAdult: isPlaybackHidden,
    } = prevPlayerStateRef.current;

    if (!trickModesEnable || isPlaybackLocked || isPlaybackHidden) {
      return;
    }

    if (!trickModes?.isPauseAllowed) {
      // this.showTrickModeNotAllowedNofication();
      return;
    }

    if (curPlaybackState === PlaybackState.PAUSED) {
      dispatch(PlayerActions.resumeRequest());
    } else if (curPlaybackState === PlaybackState.PLAYING) {
      dispatch(PlayerActions.pauseRequest());
    }
  }, [dispatch]);

  const handleRestartButton = useCallback(() => {
    const { trickModesEnable } = prevPlayerStateRef.current;

    if (!trickModesEnable) {
      return;
    }

    dispatch(PlayerActions.restartRequest());
  }, [dispatch]);

  const onMute = useCallback(
    (muted = true) => {
      setIsMuted(muted);
      dispatch(PlayerActions.setMuteState(muted));
      LocalStorage.storePlayerMuted(muted);
    },
    [dispatch]
  );

  const handleMute = useCallback(
    (value: boolean) => {
      const muted = videoRef.current?.isMuted();
      if (muted !== value) {
        videoRef.current?.setMuted(value);
        onMute(value);
      }
    },
    [onMute]
  );

  const handleVolume = useCallback(
    async (volume?: number) => {
      const localStorageVolume = await LocalStorage.getPlayerVolume();
      if (videoRef.current) {
        if (typeof volume === 'number') {
          if (volume === 0) {
            handleMute(true);
          } else {
            handleMute(false);
            videoRef.current.volume(volume);
          }
          LocalStorage.storePlayerVolume(volume);
          dispatch(PlayerActions.setVolume(volume));
        } else if (localStorageVolume) {
          return localStorageVolume;
        }
        return videoRef.current.volume() || 0;
      }
      return 0;
    },
    [dispatch, handleMute]
  );

  // after user closes the error modal
  const onErrorDismissed = useCallback(() => {
    setPlaybackError(undefined);
    onErrorCallback();
  }, [onErrorCallback]);

  // PIN callbacks
  const onPinCancel = useCallback(() => {
    dispatch(ManagePin.ActionCreators.pinReset());
    // once user cancel the PIN modal the player will be destroyed
    onErrorCallback();
  }, [dispatch, onErrorCallback]);

  const onPinConfirmation = useCallback(() => {
    if (pinSuccessCallbackRef.current) {
      dispatch(PlayerActions.unlockPlaybackByPin());
      pinSuccessCallbackRef.current();
      dispatch(ManagePin.ActionCreators.pinReset());
    }
  }, [dispatch]);

  // UI

  const customTimeout = useRef<ReturnType<typeof setTimeout>>();

  const debounce = (): void => {
    customTimeout.current = setTimeout(() => {
      setOverlayActive(false);
    }, hideControllersTimeout);
  };

  const handleMouseMove = () => {
    if (customTimeout && customTimeout.current) {
      if (!overlayActive) setOverlayActive(true);
      clearTimeout(customTimeout.current);
    }
    debounce();
  };

  useEffect(() => {
    if (isFullscreen) {
      setVideoHeight('100%');
    } else if (videoDimensions.height > window.innerHeight) {
      setVideoHeight(`${window.innerHeight - 64}px`);
    } else {
      setVideoHeight('auto');
    }
  }, [isFullscreen, videoDimensions.height]);

  const playing = playbackState === PlaybackState.PLAYING || playbackState === PlaybackState.BUFFERING;
  const loading = playbackState === PlaybackState.STREAM_LOADING || playbackState === PlaybackState.BUFFERING;
  const showSpinner = loading && !prevPlayerStateRef.current.isPlaybackLocked;

  useEffect(() => {
    if (!prevPlayerStateRef.current.isPlaybackLocked) {
      dispatch(ManagePin.ActionCreators.pinReset());
    }
  }, [dispatch]);

  // Update Stats for Nerds every second
  useEffect(() => {
    const timer = setInterval(() => {
      if (activateStats) {
        setVideoStats(videoRef.current?.getPlayerStats());
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [activateStats]);

  return (
    <div
      className={`player visible ${isFullscreen ? 'fullscreen' : ''} ${
        isFullScreenNative ? '' : 'nonNativeFullscreen'
      } ${userPressedF11 ? 'fixed' : ''} ${fillContainer ? 'fill-container' : ''}`}
      ref={wrapperRef}
      id="Player"
    >
      <div className="component hero-wrapper hero-wrapper-with-video full-bleed-hero no-background hero-video-playing">
        <div className="component-inner">
          <div className="video-wrapper" onMouseMove={handleMouseMove} id="Player_mainWrapper">
            {showSpinner && <span className="spinner" />}
            <div style={{ height: videoHeight }}>
              <NagraPlayerWrapper
                ref={videoRef}
                videoElementRef={videoHtmlElementRef}
                nagraSettings={nagraSettings}
                shakaSettings={shakaSettings}
                ssmSettings={ssmSettingsRef.current}
                onStreamReady={onStreamReady}
                onBuffering={onPlaybackBuffering}
                onPaused={onPlaybackPaused}
                onAutoPlayDenied={onAutoPlayDenied}
                onPlaying={onPlaybackPlaying}
                onPlay={onPlaybackPlay}
                onPositionUpdate={onStreamPositionUpdated}
                onError={onPlaybackError}
                onBitrateChange={onBitrateChange}
                onMute={onMute}
              />
            </div>

            <div
              className={`video-overlay ${media.controllers.disable || !overlayActive ? 'disabled' : ''}`}
              id="Player_videoOverlay"
            >
              {scrubbingProgress !== undefined ? (
                <>
                  <div className="video-overlay-container">
                    <div className="video-overlay-content">
                      <span className="video-progress video-data-progress" id="Player_progress">
                        {convertHMS(scrubbingProgress)}
                      </span>
                      <span className="video-progress video-data-separator">/</span>
                      <span className="video-progress video-data-duration" id="Player_duration">
                        {convertHMS(duration)}
                      </span>
                    </div>
                    {isVisualScrubberOn &&
                    !prevPlayerStateRef.current.isTrailerMode &&
                    scrubbingProgress !== undefined ? (
                      <VisualScrubber
                        progress={scrubbingProgress}
                        liveProgress={prevPlayerStateRef.current.liveProgress}
                        inLivePosition={getLivePositionHitState(
                          prevPlayerStateRef.current.isLiveMode,
                          scrubbingProgress,
                          prevPlayerStateRef.current.liveProgress
                        )}
                      />
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>

            <div className="video-hotspot" onClick={handlePlayButton} id="Player_videoHotspot" />
            <VideoUi
              audio={audio}
              subtitles={subtitles}
              mediaData={media}
              isFullscreen={isFullscreen}
              isPlaying={playing}
              isMuted={isMuted}
              handleHotspot={handlePlayButton}
              handleRestartButton={handleRestartButton}
              handleVolume={handleVolume}
              handleMute={handleMute}
              controllersActive={controllersActive}
              overlayActive={overlayActive}
              sliderActive={sliderActive}
              handleFullscreen={handleFullscreen}
              liveProgress={liveProgress}
              progress={progress}
              duration={duration}
              seekingStream={seekingStream}
              handleScrubberPosition={handleScrubberPosition}
              setShowWrapper={setShowWrapper}
              onScrub={setScrubbingProgress}
              videoStats={videoStats}
            />
          </div>
          <Modal
            title={translate['modal.title.error']}
            message={playbackError?.translation}
            buttons={[{ id: 'ok', text: translate.modalButtonOk, onClick: onErrorDismissed }]}
            isShowing={playbackError !== undefined}
            setIsShowing={onErrorDismissed}
          />
          <Modal notification={trickModeDisabledNotification} isShowing={trickModesModal} isFullscreen={isFullscreen} />
          <Modal isShowing={prevPlayerStateRef.current.isPlaybackLocked}>
            <Pin closeModal={onPinCancel} onPinCallback={onPinConfirmation} />
          </Modal>
        </div>
      </div>
    </div>
  );
}
